import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw, Users } from 'lucide-react';
import { api } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Cargando } from '@/components/common/Cargando';
import { useToast } from '@/hooks/useToast';

/**
 * SANEAMIENTO DE DATOS — dos diagnósticos que hasta hoy solo se podían mirar
 * con SQL a mano.
 *
 * 1 · ⚠️ R1 · Conversaciones marcadas como analizadas SIN haberlo sido.
 *
 *   Mientras `temperature` rompía la llamada al modelo, el worker de Pendientes
 *   avanzaba el cursor igual. El arreglo detiene la hemorragia hacia adelante,
 *   pero **no vuelve atrás**: esas conversaciones siguen marcadas y el worker no
 *   las va a volver a mirar nunca. Acá se cuentan y se reabren.
 *
 * 2 · A3 · Contactos duplicados.
 *
 *   Salió de normalizar los teléfonos. **Se cuentan y se muestran; no se
 *   fusionan.** Fusionar es decidir qué conversación se queda con qué historial
 *   y qué nombre sobrevive, y eso lo decide el dueño.
 *
 * ⚠️ EL NÚMERO VA ANTES DEL BOTÓN, siempre. Reabrir cuesta plata: cada
 * conversación es una llamada al modelo cuando el worker llegue a ella. Un botón
 * que no dice a cuántas va a tocar es un botón que nadie debería apretar.
 */

interface Conteo {
  conversaciones: number;
  pendientesHoy: number;
  corte: string;
}

interface Duplicados {
  grupos: number;
  sobrantes: number;
  contactosConTelefono: number;
  detalle: Array<{
    telefono: string;
    canalNombre: string;
    cuantos: number;
    nombres: string[];
  }>;
}

export function Saneamiento() {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirmando, setConfirmando] = useState(false);

  const conteo = useQuery({
    queryKey: ['reanalisis-pendientes'],
    queryFn: () => api.get<Conteo>('/api/reanalisis-pendientes'),
  });

  const duplicados = useQuery({
    queryKey: ['contactos-duplicados'],
    queryFn: () => api.get<Duplicados>('/api/contactos-duplicados'),
  });

  const reabrir = useMutation({
    mutationFn: () => api.post<{ reabiertas: number }>('/api/reanalisis-pendientes', {}),
    onSuccess: (r) => {
      toast.success(
        `${r.reabiertas} conversación${r.reabiertas === 1 ? '' : 'es'} vuelta${
          r.reabiertas === 1 ? '' : 's'
        } a la fila. El análisis va a ir saliendo de a poco.`,
      );
      setConfirmando(false);
      void qc.invalidateQueries({ queryKey: ['reanalisis-pendientes'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const c = conteo.data;
  const d = duplicados.data;

  return (
    <div className="space-y-4">
      {/* ── R1 */}
      <div className="rounded-lg border border-surface-border p-4 dark:border-slate-800">
        <div className="mb-2 flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Conversaciones que quedaron sin analizar de verdad
          </h3>
        </div>

        {conteo.isLoading || !c ? (
          <Cargando />
        ) : c.conversaciones === 0 ? (
          <p className="text-sm text-slate-500">
            Ninguna quedó en ese estado. No hay nada que hacer acá.
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Hay <strong>{c.conversaciones}</strong> conversacion
              {c.conversaciones === 1 ? '' : 'es'} marcada{c.conversaciones === 1 ? '' : 's'} como
              analizada{c.conversaciones === 1 ? '' : 's'} antes de que se arreglara la detección.
              Mientras estuvo rota, el sistema las marcaba igual aunque no las mirara. Hoy hay{' '}
              <strong>{c.pendientesHoy}</strong> pendiente{c.pendientesHoy === 1 ? '' : 's'}{' '}
              detectado{c.pendientesHoy === 1 ? '' : 's'} en total.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Volverlas a la fila hace que la detección las lea de nuevo. No se borra nada: los
              pendientes que ya están siguen ahí, y el sistema no los repite. Sí consume IA —una
              lectura por conversación— y va a tardar varias vueltas.
            </p>
            <Button className="mt-3" size="sm" onClick={() => setConfirmando(true)}>
              Volverlas a la fila
            </Button>
          </>
        )}
      </div>

      {/* ── Duplicados */}
      <div className="rounded-lg border border-surface-border p-4 dark:border-slate-800">
        <div className="mb-2 flex items-center gap-2">
          <Users className="h-4 w-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Clientes que pueden estar entrando dos veces
          </h3>
        </div>

        {duplicados.isLoading || !d ? (
          <Cargando />
        ) : d.grupos === 0 ? (
          <p className="text-sm text-slate-500">
            No hay teléfonos repetidos. De {d.contactosConTelefono} contactos con teléfono, cada
            uno entra una sola vez.
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              <strong>{d.sobrantes}</strong> contacto{d.sobrantes === 1 ? '' : 's'} de más, en{' '}
              <strong>{d.grupos}</strong> teléfono{d.grupos === 1 ? '' : 's'} repetido
              {d.grupos === 1 ? '' : 's'} dentro del mismo canal. Es el mismo cliente guardado dos
              veces: se le contesta por separado, tiene dos historiales, y los números lo cuentan
              doble.
            </p>
            <ul className="mt-2 space-y-1">
              {d.detalle.slice(0, 10).map((g) => (
                <li key={`${g.telefono}-${g.canalNombre}`} className="text-xs text-slate-500">
                  <span className="font-mono">{g.telefono}</span> · {g.canalNombre} ·{' '}
                  {g.cuantos} veces · {g.nombres.slice(0, 3).join(' / ')}
                </li>
              ))}
            </ul>
            {/* ⚠️ Se dice explícitamente que no hay botón, y por qué. Sin esto,
                quien lea esta lista va a buscar el botón de "unir" y va a creer
                que falta algo. */}
            <p className="mt-3 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              Esto solo los muestra. Unir dos contactos es decidir cuál conversación se queda con
              qué historial y qué nombre sobrevive — eso no lo puede decidir el sistema.
            </p>
          </>
        )}
      </div>

      <Modal
        open={confirmando}
        onClose={() => setConfirmando(false)}
        title="¿Volverlas a la fila?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmando(false)}>
              Cancelar
            </Button>
            <Button onClick={() => reabrir.mutate()} disabled={reabrir.isPending}>
              Sí, volver {c?.conversaciones ?? 0} a la fila
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Se van a marcar <strong>{c?.conversaciones ?? 0}</strong> conversaciones para que la
          detección las lea de nuevo.
        </p>
        <ul className="mt-2 space-y-1 text-xs text-slate-500">
          <li>· No se borra ningún pendiente de los que ya están.</li>
          <li>· Los que se vuelvan a detectar no se duplican.</li>
          <li>· Consume IA: una lectura por conversación, de a poco.</li>
        </ul>
      </Modal>
    </div>
  );
}
